import React from 'react';
import PageHero from '../components/layout/PageHero';

const AccessibilityStatementPage = () => {
  return (
    <div className="min-h-screen" data-testid="accessibility-statement-page">
      <PageHero
        eyebrow="Legal"
        title="Accessibility Statement"
        description="Our commitment to making AUTOXIUM usable by everyone."
      />
      <section className="py-12 md:py-16">
        <div className="max-w-screen-2xl mx-auto px-4 md:px-8">
          <div className="max-w-3xl prose-autoxium">
            <p className="text-sm text-muted-foreground">
              Last updated: September 26, 2026 · Last reviewed: September 26, 2026
            </p>

            <h2>Our Commitment</h2>
            <p>
              AUTOXIUM is committed to providing a platform that's accessible to the widest
              possible audience, regardless of technology or ability. We work to ensure
              that Articles, Tools, and Community features can be navigated with a
              keyboard, read with assistive technology, and used comfortably by people
              with visual, motor, auditory, or cognitive disabilities.
            </p>

            <h2>Standard We Target</h2>
            <p>
              We aim to meet the <strong>Web Content Accessibility Guidelines (WCAG) 2.2
              Level AA</strong> across the site. That includes sufficient color contrast,
              visible focus indicators on every interactive element, semantic headings and
              landmarks, keyboard-operable navigation and forms, adequately sized touch
              targets, and text alternatives for meaningful images and icons.
            </p>

            <h2>Conformance Status</h2>
            <p>
              AUTOXIUM is <strong>partially conformant</strong> with WCAG 2.2 Level AA:
              most of the site meets the standard, but some content does not yet fully
              conform (see Known Limitations below).
            </p>

            <h2>How We Assessed This Site</h2>
            <p>
              This statement is based on a self-assessment by the AUTOXIUM team, using
              keyboard-only navigation, browser accessibility tooling, and manual checks
              at desktop and mobile viewport sizes. It has not yet been verified by an
              independent third-party audit. New pages and tools are reviewed for keyboard
              operability and screen-reader compatibility before release.
            </p>

            <h2>Known Limitations</h2>
            <ul>
              <li>
                <strong>Dense tool output.</strong> Some interactive engineering tools (for
                example, the E2E and CRC calculators) present dense tabular output that may
                be easier to use with a screen magnifier than a screen reader today.
              </li>
              <li>
                <strong>Charts in simulators and calculators.</strong> Graphs (for example,
                in the EV Powertrain and Charging simulators) do not yet offer a full text or
                data-table alternative. The key results are also shown as numbers alongside
                each chart; the drag-to-zoom interaction requires a pointer.
              </li>
              <li>
                <strong>Technical diagrams.</strong> Some diagrams in older Articles and How
                It Works topics have brief alternative text that does not describe every
                detail of the figure; the surrounding text explains the concept.
              </li>
            </ul>
            <p>We're actively working to remove these limitations.</p>

            <h2>Feedback &amp; Contact</h2>
            <p>
              If you encounter an accessibility barrier anywhere on AUTOXIUM, or need
              content in an alternative format, please email{' '}
              <a href="mailto:contact@autoxium.com">contact@autoxium.com</a> (subject line
              "Accessibility"). Include the page URL and a description of the issue.
            </p>
            <p>
              We aim to acknowledge accessibility feedback within <strong>5 business
              days</strong> and to propose a fix or an accessible alternative within
              <strong> 15 business days</strong>.
            </p>

            <h2>Escalation</h2>
            <p>
              If you're not satisfied with our response, you can contact the accessibility
              enforcement or equality body in your country (for example, the national
              authority responsible for the European Accessibility Act in EU member states).
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AccessibilityStatementPage;
